import { useTranslation } from "react-i18next";
import {
  AutoComplete,
  FormContainer,
  ISelectType,
  SwitchInput,
  Treturn,
  useFetch,
} from "@4uhub/lib4uhub";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Grid } from "@mui/material";
import { IInsuranceCategoryProps } from "./models";
import {
  createTicketAutomationReceivedRuleInsuranceCategory,
  getTicketAutomationReceivedRuleInsuranceCategoryById,
  insuranceRegisterList,
  updateTicketAutomationReceivedRuleInsuranceCategoryById,
} from "../../../../../../../services/ticketAutomationReceivedRuleInsurance.service";
import {
  insuranceCategorySchema,
  TInsuranceCategoryForm,
} from "./insuranceCategorySchema";

const translationPath =
  "chatBot.page.automations.automationReceivedRules.automationReceivedRuleInsuranceCategory.";

const AutomationReceivedRuleInsuranceCategory = ({
  id,
  ticketAutomationReceivedRuleId,
  onBackButton,
}: IInsuranceCategoryProps) => {
  const { t } = useTranslation();

  const { sendRequest: save, loading: sLoading } = useFetch(
    createTicketAutomationReceivedRuleInsuranceCategory,
  );

  const { sendRequest: update, loading: uLoading } = useFetch(
    updateTicketAutomationReceivedRuleInsuranceCategoryById,
  );

  const { sendRequest: get, loading: gLoading } = useFetch(
    getTicketAutomationReceivedRuleInsuranceCategoryById,
  );

  const updateHandler = (v: TInsuranceCategoryForm, id: string) =>
    update({
      item: {
        id: id,
        ...v,
        insuranceExternalName: v.insuranceRegister.name,
        insuranceExternalId: v.insuranceRegister.id,
        enable: v.enable,
        ticketAutomationReceivedRuleId: ticketAutomationReceivedRuleId ?? "",
      },
      id: id,
    });

  const saveHandler = (v: TInsuranceCategoryForm) =>
    save({
      ...v,
      insuranceExternalName: v.insuranceRegister.name,
      insuranceExternalId: v.insuranceRegister.id,
      enable: v.enable,
      ticketAutomationReceivedRuleId: ticketAutomationReceivedRuleId ?? "",
    });

  const getHandler = useCallback(
    async (id: string): Promise<Treturn<TInsuranceCategoryForm>> => {
      const { data, success } = await get(id);

      if (data) {
        const newData: TInsuranceCategoryForm = {
          ...data,
          insuranceRegister: {
            id: data.insuranceExternalId,
            name: data.insuranceExternalName,
          },
          enable: data.enable,
        };
        return { data: newData, success: true };
      }
      return { data, success };
    },
    [get],
  );

  const [
    optionsTicketAutomationReceivedSlotCalendars,
    setTicketAutomationReceivedSlotCalendars,
  ] = useState<ISelectType[]>([]);

  const { sendRequest } = useFetch(insuranceRegisterList);

  const params = useMemo(() => {
    return {
      EnableCode: true,
    };
  }, []);

  const fetchTicketAutomationReceivedSlotCalendars = useCallback(async () => {
    const { data, success } = await sendRequest(params);
    if (data && success) {
      setTicketAutomationReceivedSlotCalendars(
        data.map((d) => ({
          id: d.identifier.toString(),
          name: d.insuranceName,
        })),
      );
    }
  }, [sendRequest, params]);

  useEffect(() => {
    fetchTicketAutomationReceivedSlotCalendars();
  }, [fetchTicketAutomationReceivedSlotCalendars]);

  const loading = sLoading || uLoading || gLoading;

  return (
    <FormContainer<TInsuranceCategoryForm>
      saveFunction={saveHandler}
      updateFunction={updateHandler}
      getFunction={getHandler}
      loading={loading}
      title={t(translationPath + "insurance")}
      schema={insuranceCategorySchema}
      subtitle={t(translationPath + "new_insurance")}
      backButtonOnClick={onBackButton}
      redirectOnSave={false}
      onSuccess={onBackButton}
      isModalForm={true}
      propId={id}
      formActionsOnBottom={false}
      fields={() => (
        <Grid container spacing={2}>
          <Grid item sm={12} textAlign={"end"}>
            <SwitchInput name="enable" label={t(translationPath + "enable")} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <AutoComplete
              fullWidth
              options={optionsTicketAutomationReceivedSlotCalendars}
              name="insuranceRegister"
              label={t(translationPath + "insurance")}
              getOptionLabel={(option) => option.name}
              size="small"
            />
          </Grid>
        </Grid>
      )}
    />
  );
};

export default AutomationReceivedRuleInsuranceCategory;
